import React, { useEffect, useRef, useState } from 'react';
import { LuCornerDownLeft, LuSearch, LuSparkle, LuX } from 'react-icons/lu';

import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { UniversalSearchResultView, useUniversalSearch } from '@/components/UniversalSearchResults';
import { cn } from '@/utils/cn';

const UNIVERSAL_SEARCH_INPUT_SELECTOR = '[data-claro-search-input="true"]';
const EXPANDED_SEARCH_VIEW_THRESHOLD = 400;
const FALLBACK_SEARCH_VIEW_INSET = 24;

interface PopoverLayout {
    align: 'start' | 'end';
    offsetX?: number;
    width?: number;
}

const parsePixels = (value: string) => Number.parseFloat(value) || 0;

const getSearchPopoverLayout = (
    anchorElement: HTMLElement,
): PopoverLayout & { boundaryElement: HTMLElement | null } => {
    const anchorRect = anchorElement.getBoundingClientRect();
    const anchorWidth = Math.round(anchorRect.width);

    if (anchorWidth >= EXPANDED_SEARCH_VIEW_THRESHOLD) {
        return {
            align: 'end',
            width: anchorWidth,
            boundaryElement: null,
        };
    }

    let currentElement = anchorElement.parentElement;
    while (currentElement && currentElement !== document.body) {
        const rect = currentElement.getBoundingClientRect();
        const styles = window.getComputedStyle(currentElement);
        const paddingLeft = parsePixels(styles.paddingLeft);
        const paddingRight = parsePixels(styles.paddingRight);
        const contentLeft = rect.left + paddingLeft;
        const contentRight = rect.right - paddingRight;
        const contentWidth = Math.round(contentRight - contentLeft);
        const containsAnchor = contentLeft <= anchorRect.left && contentRight >= anchorRect.right;

        if (paddingLeft + paddingRight >= FALLBACK_SEARCH_VIEW_INSET && containsAnchor && contentWidth > anchorWidth) {
            return {
                align: 'start',
                width: contentWidth,
                offsetX: Math.round(contentLeft - anchorRect.left),
                boundaryElement: currentElement,
            };
        }

        currentElement = currentElement.parentElement;
    }

    const viewportWidth = window.innerWidth;
    const fallbackWidth = Math.max(anchorWidth, viewportWidth - FALLBACK_SEARCH_VIEW_INSET * 2);
    const fallbackLeft = Math.max(FALLBACK_SEARCH_VIEW_INSET, (viewportWidth - fallbackWidth) / 2);

    return {
        align: 'start',
        width: Math.round(fallbackWidth),
        offsetX: Math.round(fallbackLeft - anchorRect.left),
        boundaryElement: document.documentElement,
    };
};

const isSamePopoverLayout = (left: PopoverLayout, right: PopoverLayout) =>
    left.align === right.align && left.offsetX === right.offsetX && left.width === right.width;

const getHotkeyHint = () => {
    if (typeof navigator === 'undefined') {
        return '⌘K';
    }

    return /(Mac|iPhone|iPad)/i.test(navigator.platform) ? '⌘K' : 'Ctrl K';
};

export const focusUniversalSearchInput = () => {
    const input = Array.from(document.querySelectorAll<HTMLInputElement>(UNIVERSAL_SEARCH_INPUT_SELECTOR)).find(
        (element) => element.offsetParent !== null && !element.disabled,
    );

    if (!input) {
        return false;
    }

    input.focus();
    input.select();
    return true;
};

interface UniversalSearchButtonProps {
    className?: string;
}

export const UniversalSearchButton = ({ className }: UniversalSearchButtonProps) => {
    const searchState = useUniversalSearch();
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const [popoverLayout, setPopoverLayout] = useState<PopoverLayout>({ align: 'end' });

    useEffect(() => {
        const anchorElement = anchorRef.current;
        if (!anchorElement || typeof ResizeObserver === 'undefined') {
            return;
        }

        let observedBoundaryElement: HTMLElement | null = null;

        const updateLayout = () => {
            const { boundaryElement, ...nextLayout } = getSearchPopoverLayout(anchorElement);
            setPopoverLayout((currentLayout) =>
                isSamePopoverLayout(currentLayout, nextLayout) ? currentLayout : nextLayout,
            );

            if (observedBoundaryElement && observedBoundaryElement !== boundaryElement) {
                resizeObserver.unobserve(observedBoundaryElement);
            }

            if (boundaryElement && observedBoundaryElement !== boundaryElement) {
                resizeObserver.observe(boundaryElement);
            }

            observedBoundaryElement = boundaryElement;
        };

        const resizeObserver = new ResizeObserver(updateLayout);
        resizeObserver.observe(anchorElement);
        updateLayout();

        window.addEventListener('resize', updateLayout);

        return () => {
            window.removeEventListener('resize', updateLayout);
            if (observedBoundaryElement) {
                resizeObserver.unobserve(observedBoundaryElement);
            }
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <Popover open={searchState.open} onOpenChange={searchState.handleOpenChange}>
            <PopoverAnchor asChild>
                <div ref={anchorRef} className={cn('relative min-w-0', className)}>
                    <LuSearch className="text-cocoa-500 pointer-events-none absolute top-1/2 left-4 h-[18px] w-[18px] -translate-y-1/2" />
                    <Input
                        type="text"
                        role="combobox"
                        aria-expanded={searchState.open}
                        aria-controls={searchState.dropdownId}
                        aria-activedescendant={searchState.activeDescendantId}
                        aria-autocomplete="list"
                        placeholder="Search clients, accounts, organizations…"
                        aria-label="Search clients, accounts, organizations"
                        title="Search clients, accounts, organizations"
                        autoComplete="off"
                        data-claro-search-input="true"
                        value={searchState.search}
                        onChange={searchState.handleChange}
                        onFocus={searchState.handleFocus}
                        onKeyDown={searchState.handleKeyDown}
                        className={cn(
                            'border-cocoa-100 bg-cocoa-0 text-cocoa-900 placeholder:text-cocoa-500 h-11 rounded-[14px] pr-44 pl-11 shadow-none',
                            'hover:border-cocoa-200 focus-visible:border-gold-200 focus-visible:ring-gold-100/70 focus-visible:ring-[3px]',
                        )}
                    />
                    <div className="absolute top-1/2 right-3 flex -translate-y-1/2 items-center gap-2">
                        {searchState.search ? (
                            <button
                                type="button"
                                aria-label="Clear search"
                                className="text-cocoa-500 hover:text-cocoa-900 focus-visible:ring-cocoa-300 inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-full transition-colors focus-visible:ring-[3px] focus-visible:outline-none"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={searchState.handleClear}
                            >
                                <LuX className="h-3.5 w-3.5" />
                            </button>
                        ) : (
                            <span className="text-cocoa-500 border-cocoa-100 rounded-full border bg-white px-2 py-0.5 text-[11px] font-medium">
                                {getHotkeyHint()}
                            </span>
                        )}
                        <button
                            type="button"
                            aria-label="Ask Claire&#8482;"
                            disabled={!searchState.search.trim()}
                            className={cn(
                                'border-cocoa-100 text-cocoa-700 hover:bg-cocoa-50 hover:text-cocoa-900',
                                'focus-visible:ring-gold-100 inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-full border',
                                'bg-white px-2.5 text-[11px] font-thin transition-colors',
                                'focus-visible:ring-[3px] focus-visible:outline-none',
                                'disabled:cursor-default disabled:opacity-50 disabled:hover:bg-white',
                            )}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={searchState.handleAskAi}
                        >
                            <LuSparkle className="text-gold-300 h-3 w-3" />
                            <span>Ask Claire&#8482;</span>
                            <span className="text-cocoa-500 inline-flex items-center gap-0.5">
                                <span className="text-[10px]">⌘</span>
                                <LuCornerDownLeft className="h-2.5 w-2.5" />
                            </span>
                        </button>
                    </div>
                </div>
            </PopoverAnchor>
            <PopoverContent
                align={popoverLayout.align}
                sideOffset={8}
                className="w-(--radix-popover-trigger-width) border-none bg-transparent p-0 shadow-none"
                onOpenAutoFocus={(event) => event.preventDefault()}
                onFocusOutside={(event) => {
                    const target = event.target as HTMLElement | null;
                    if (target?.closest(UNIVERSAL_SEARCH_INPUT_SELECTOR)) {
                        event.preventDefault();
                    }
                }}
                onInteractOutside={(event) => {
                    const target = event.target as HTMLElement | null;
                    if (target?.closest(UNIVERSAL_SEARCH_INPUT_SELECTOR)) {
                        event.preventDefault();
                    }
                }}
                style={
                    popoverLayout.width
                        ? {
                              width: `${popoverLayout.width}px`,
                              marginLeft: popoverLayout.offsetX ? `${popoverLayout.offsetX}px` : undefined,
                          }
                        : undefined
                }
            >
                <UniversalSearchResultView
                    activeTab={searchState.activeTab}
                    activeIndex={searchState.activeIndex}
                    announcement={searchState.announcement}
                    dropdownId={searchState.dropdownId}
                    error={searchState.error}
                    groups={searchState.groups}
                    query={searchState.query}
                    recentItems={searchState.recentItems}
                    showEmptyState={searchState.showEmptyState}
                    showFirstQuerySkeleton={searchState.showFirstQuerySkeleton}
                    showNoResults={searchState.showNoResults}
                    showRefreshingProgress={searchState.showRefreshingProgress}
                    tabPanelId={searchState.tabPanelId}
                    totalResults={searchState.totalResults}
                    visibleGroups={searchState.visibleGroups}
                    visibleItems={searchState.visibleItems}
                    onAskAi={searchState.handleAskAi}
                    onRetry={searchState.handleRetry}
                    onRowHover={searchState.handleRowHover}
                    onSelectItem={searchState.handleSelectItem}
                    onTabChange={searchState.handleTabChange}
                />
            </PopoverContent>
        </Popover>
    );
};
